

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { useDisabledAnchor } from '../../hooks/use-disabled-anchor';
import { useDisabledState } from '../../hooks/use-disabled-state';
import { ButtonGroupContext } from '../button-group/context/button-group.context';
import { Slot } from '../slot';
import { TextButtonRootContext } from './context/text-button-root.context';
import { textButtonRootRecipe } from './styles/text-button-root.styles';
import type { TextButtonRootPropsInterface } from './types/text-button-root.types';

/**
 * @since 10.0.0
 */

export const TextButtonRoot = forwardRef<HTMLButtonElement, TextButtonRootPropsInterface>(
  (props: TextButtonRootPropsInterface, forwardedRef) => {
    const groupContext = useContext(ButtonGroupContext);
    const isGrouped = Object.keys(groupContext).length > 0;
    const size = groupContext?.size || 'md';

    // Destructuring from `props` first so we can apply custom type to `textStyle`.
    const {
      asChild = false,
      tone = 'brand',
      children,
      className,
      css: cssProp,
      disabled = false,
      onImpact = false,
      textStyle = 'body-bold',
      type = 'button',
      ...rest
    } = props;

    const styles = css(
      textButtonRootRecipe.raw({
        tone,
        disabled,
        onImpact,
        isGrouped,
        size,
      }),
      cssProp,
    );

    const Component = asChild ? Slot : 'button';

    const a11yDisabledState = useDisabledState({ disabled, focusable: true });

    const content = useDisabledAnchor({
      disabled,
      asChild,
      children,
      a11yProps: a11yDisabledState,
    });

    return (
      <TextButtonRootContext.Provider value={{ tone, disabled, onImpact, size, textStyle }}>
        <Component
          ref={forwardedRef}
          data-c11n-component="TextButton.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'group', styles, className)}
          type={asChild ? undefined : type}
          {...rest}
          {...a11yDisabledState}
        >
          {content}
        </Component>
      </TextButtonRootContext.Provider>
    );
  },
);

TextButtonRoot.displayName = 'TextButton.Root';
